"use client";

import { useEffect } from "react";
import { useNetworkDebugEnabled } from "@/components/NetworkDebugBar";

/**
 * Dev-only remote console (see `app/api/debug/remote/route.ts`): while the
 * network debug flag is on, long-polls the dev server for JavaScript sent with
 * `scripts/remote.mjs`, runs it here and posts the result back — so code can
 * be tried on a phone without typing it into Eruda. Compiled out of
 * production builds.
 */
export default function RemoteDebugClient() {
  const enabled = useNetworkDebugEnabled();

  useEffect(() => {
    if (process.env.NODE_ENV === "production" || !enabled) return;
    const abort = new AbortController();
    const { signal } = abort;
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    (async () => {
      while (!signal.aborted) {
        try {
          const res = await fetch("/api/debug/remote", { signal, cache: "no-store" });
          // 401 before sign-in, 404 if the route is gone: back off, keep trying.
          if (res.status !== 200) {
            await sleep(res.status === 204 ? 0 : 5000);
            continue;
          }
          const { commands } = (await res.json()) as { commands: { id: string; code: string }[] };
          for (const c of commands) {
            const result = await run(c.code);
            await fetch("/api/debug/remote", {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id: c.id, ...result, device: navigator.userAgent }),
              signal,
            });
          }
        } catch {
          if (!signal.aborted) await sleep(3000);
        }
      }
    })();
    return () => abort.abort();
  }, [enabled]);

  return null;
}

/** Console-like evaluation: an expression's value, or a statement block's
 *  `return`; top-level `await` works in both. */
async function run(code: string): Promise<{ ok: boolean; value: string }> {
  try {
    let fn: () => Promise<unknown>;
    try {
      fn = new Function(`return (async () => (${code}\n))()`) as () => Promise<unknown>;
    } catch {
      fn = new Function(`return (async () => {${code}\n})()`) as () => Promise<unknown>;
    }
    return { ok: true, value: describe(await fn()) };
  } catch (err) {
    return { ok: false, value: err instanceof Error ? `${err.name}: ${err.message}\n${err.stack ?? ""}` : String(err) };
  }
}

function describe(value: unknown): string {
  if (value === undefined) return "undefined";
  if (typeof value === "string") return value;
  if (value instanceof Element) return value.outerHTML.slice(0, 4000);
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}
